/**
 * Query-key factory for thread attachment metadata.
 *
 * Lives apart from the hook (the `broadcast-keys.ts` / `creative-keys.ts` idiom)
 * so `lib/supabase-utils.ts` can invalidate by the factory rather than a
 * hand-typed literal. The hook imports `getSupabaseFunctionError` from that
 * module, so keeping the keys in the hook file would make the import cycle.
 *
 * Both scope dimensions are load-bearing, not decoration. The two reads behind
 * this key (`email_metadata`, `email_attachments`) are RLS-scoped per viewer, so
 * the cached Map is a per-(org, user) projection: keying it on provider email
 * ids alone lets one renderer serve org A's attachment filenames to a session
 * that has since switched to org B — a real window in any long-lived renderer
 * (native webview, desktop shell, a browser tab left open across an org switch),
 * where nothing forces a remount to clear the entry.
 *
 * `all` is the invalidation prefix `invalidateEmailCaches` pokes; keep it the
 * leading segment so a prefix invalidation keeps matching every scope. The ids
 * csv is always the LAST segment — readers derive it with `key.at(-1)` rather
 * than a hard-coded offset, so adding a scope dimension cannot silently make
 * them read the wrong segment.
 *
 * The params stay `string | undefined` because `useQuery` needs a key even while
 * the query is disabled; the hook's `enabled` gate is what keeps an unscoped key
 * from ever holding data.
 */
export const emailThreadAttachmentKeys = {
  all: ['email-thread-attachments'] as const,
  scope: (orgId: string | undefined, userId: string | undefined) =>
    [...emailThreadAttachmentKeys.all, orgId, userId] as const,
  /** `sortedIdsCsv` is the provider email ids, sorted then comma-joined. */
  byEmailIds: (orgId: string | undefined, userId: string | undefined, sortedIdsCsv: string) =>
    [...emailThreadAttachmentKeys.scope(orgId, userId), sortedIdsCsv] as const,
} as const;
